import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";

const BrandingContext = createContext(null);

const DEFAULT_LOGIN = {
  app_name: "CINNABIT",
  description: "Cincin Irrigasi Berbasis IoT — monitoring lahan & kendali irigasi real-time.",
  extra_info: "Powered by USK · BRIN · UTU",
  icon_url: null,
};

const DEFAULT_APP = { app_name: "CINNABIT", icon_url: null };

export function BrandingProvider({ children }) {
  const [globalLogin, setGlobalLogin] = useState(DEFAULT_LOGIN);
  const [globalApp, setGlobalApp] = useState(DEFAULT_APP);
  const [projectBranding, setProjectBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  const { currentId } = useProject();

  const refreshGlobal = useCallback(async () => {
    setLoading(true);
    try {
      const [l, a] = await Promise.all([
        api.get("/settings/login").catch(() => ({ data: DEFAULT_LOGIN })),
        api.get("/settings/app").catch(() => ({ data: DEFAULT_APP })),
      ]);
      setGlobalLogin(l.data);
      setGlobalApp(a.data);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshProject = useCallback(async (id) => {
    if (!id) { setProjectBranding(null); return; }
    try {
      const { data } = await api.get(`/projects/${id}/branding`);
      setProjectBranding(data);
    } catch {
      setProjectBranding(null);
    }
  }, []);

  useEffect(() => { refreshGlobal(); }, [refreshGlobal]);
  useEffect(() => { refreshProject(currentId); }, [currentId, refreshProject]);

  // Merge: project branding takes priority when present
  const app = projectBranding ? projectBranding.app : globalApp;
  const login = projectBranding ? projectBranding.login : globalLogin;
  const overrides = projectBranding?.overrides || {};
  const source = projectBranding?.source || "global";

  // Update document title whenever name changes
  useEffect(() => {
    if (app?.app_name) document.title = app.app_name;
  }, [app]);

  return (
    <BrandingContext.Provider value={{
      login,
      app,
      globalLogin,
      globalApp,
      projectBranding,
      overrides,
      source,
      loading,
      refresh: refreshGlobal,
      refreshProject: () => refreshProject(currentId),
    }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error("useBranding must be used within BrandingProvider");
  return ctx;
}
